import { useState, useEffect } from "react";
import {
    Backdrop, Button, Card, CardActions, CardContent, CardMedia, CircularProgress,
    Container, FormControl, Grid, InputLabel, Select, TextField, Box
} from "@mui/material";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { DataRef } from "../../../../types/DataRef";
import CheckAttr from "../../../../utils/CheckAttr";
import Cookies from "universal-cookie";
import axios, { AxiosInstance } from "axios";
import { AlertType } from "../../../../types/AlertType";
import { SnackBarAlert } from "../../../element/SnackBarAlert";
import { downloadFile } from "../../../../utils/utils";
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { loggoutUser } from "../../../../redux/actions/logginUser";

interface EXTRACTION {
    pays?: number;
    bu?: number;
    client?: number;
    operation?: number;
    date_debut: string;
    date_fin: string;
}

interface InputValidation {
    date_debut: boolean;
    date_fin: boolean;
}

const initialData: EXTRACTION = {
    pays: undefined,
    bu: undefined,
    client: undefined,
    operation: undefined,
    date_debut: '',
    date_fin: ''
};

const resetInpuValidation: InputValidation = {
    date_debut: false,
    date_fin: false
};

const urlBaseApi = process.env.REACT_APP_BASE_API_URL as string;

const ExtractionSaisie = () => {
    const cookies = new Cookies();
    const token: string = cookies.get("jwt");

    const http: AxiosInstance = axios.create({
        baseURL: urlBaseApi,
        // timeout: 1000,
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    const navigate = useNavigate();
    const dispatch = useDispatch();


    // Add global response interceptor
    http.interceptors.response.use(
        (response) => {
            // Modify response data here, if needed
            return response;
        },
        (error) => {
            console.log(error.message);
            if (error.message === "Request failed with status code 401") {
                // Remove the redux store
                dispatch(loggoutUser());

                // Remove token in navigator cookies
                cookies.remove('jwt', { path: '/' });

                navigate("/sign-in");
            }
            return Promise.reject(error);
        }
    );

    const [loader, setLoader] = useState<boolean>(false);

    const [alertMessage, setAlerteMessage] = useState<string>('');
    const [openSnack, setOpenSnack] = useState<boolean>(false);
    const [alertType, setAlertType] = useState<AlertType>("success");

    const [dataPays, setDataPays] = useState<DataRef[]>([]);
    const [dataBusinessUnits, setDataBusinessUnits] = useState<DataRef[]>([]);
    const [dataClients, setDataClients] = useState<DataRef[]>([]);
    const [dataOperations, setDataOperations] = useState<DataRef[]>([]);

    const [extractionDatas, setExtractionDatas] = useState<EXTRACTION>(initialData);

    const [errorInput, setErrorInput] = useState<InputValidation>(resetInpuValidation);

    useEffect(() => {
        http.get('saisie/manager/get-pays')
            .then(res => {
                Array.isArray(res.data) && setDataPays(res.data);
            }).catch(error => {
                console.log(error);
            });
    }, []);

    const getBusinessUnitByPays = (pays: any): void => {
        setDataBusinessUnits([]);
        setDataClients([]);
        setDataOperations([]);


        const val = CheckAttr(pays) ? pays : undefined;
        setExtractionDatas({ ...initialData, pays: val });

        if (val) {
            http.get(`saisie/manager/get-bus/${pays}`)
                .then(res => {
                    Array.isArray(res.data) && setDataBusinessUnits(res.data);
                }).catch(error => {
                    console.log(error);
                });
        }
    };

    const getClientByPaysBu = (bu: any): void => {
        setDataClients([]);
        setDataOperations([]);

        setExtractionDatas({
            ...extractionDatas,
            bu,
            client: undefined,
            operation: undefined
        });

        http.get(`client/find-by-pays-bu/${extractionDatas?.pays}/${bu}`)
            .then(res => {
                Array.isArray(res.data) && setDataClients(res.data);
            }).catch(error => {
                console.log(error);
            });
    };

    const getOperationByClient = (client: any): void => {
        setDataOperations([]);

        setExtractionDatas({
            ...extractionDatas,
            client,
            operation: undefined
        });

        http.post(`operation/get-operations-for-extraction`, {
            pays: extractionDatas?.pays,
            bu: extractionDatas?.bu,
            client
        }).then(res => {
            Array.isArray(res.data) && setDataOperations(res.data);
        }).catch(error => {
            console.log(error);
        });
    };

    const handleChange = (e: any) => {
        const { name, value } = e.target;

        switch (name) {
            case 'pays':
                getBusinessUnitByPays(value);
                break;
            case 'bu':
                getClientByPaysBu(value);
                break;
            case 'client':
                getOperationByClient(value);
                break;
            default:
                setExtractionDatas({
                    ...extractionDatas,
                    [name]: value
                });
        };
    };

    const isValidate = (): boolean => {
        // Reinitialise les variables des validations
        setErrorInput(resetInpuValidation);

        /**
         * Verification si date debut ou date fin est vide
         */
        if (!CheckAttr(extractionDatas?.date_debut) || !CheckAttr(extractionDatas?.date_fin)) {
            if (!CheckAttr(extractionDatas?.date_debut)) {
                setErrorInput((prev: InputValidation) => ({ ...prev, date_debut: true }));
            }

            if (!CheckAttr(extractionDatas?.date_fin)) {
                setErrorInput((prev: InputValidation) => ({ ...prev, date_fin: true }));
            }

            setAlerteMessage("Merci de remplir les champs en rouge SVP!");
            setAlertType("warning");
            setOpenSnack(true);

            return false;
        }

        /**
         * Verification si date debut est supérieur au date fin
         */
        const dateDeb = new Date(extractionDatas?.date_debut);
        const dateFin = new Date(extractionDatas?.date_fin);
        if (dateDeb.getTime() > dateFin.getTime()) {
            setErrorInput({
                date_debut: true,
                date_fin: true
            });

            setAlerteMessage("Désolé! La date fin doit être supèrieur ou égal à la date debut !");
            setAlertType("warning");
            setOpenSnack(true);

            return false;
        }

        return true;
    }

    const handleExtract = () => {
        if (isValidate()) {
            setLoader(true);
            http.post("extraction/saisie", extractionDatas)
                .then(res => {
                    // On télécharge le fichier
                    const { sucess, filename } = res.data;
                    sucess && downloadFile(filename as string);

                    setAlerteMessage("Extraction efféctuée avec succès !");
                    setAlertType("success");
                    setOpenSnack(true);
                })
                .catch((error) => {
                    console.log(error);
                    setAlertType("error");
                    setOpenSnack(true);
                })
                .finally(() => {
                    setLoader(false);
                });
        }
    }

    const handleCloseAlert = () => {
        setOpenSnack(false)
    }

    const stackAlert: JSX.Element = SnackBarAlert(openSnack, handleCloseAlert, alertType, alertMessage);

    return (
        <>
            <Container maxWidth="md" sx={{ mt: 10 }}>
                <Card sx={{ maxWidth: 845 }}>
                    <CardMedia
                        component="img"
                        image="/images/extraction_saisie.png"
                        alt="green iguana"
                        sx={{ display: { xs: 'none', sm: 'flex' }, height: 140 }}
                    />
                    <CardContent>
                        <Grid container spacing={2} columns={{ xs: 6, sm: 12, md: 12 }}>
                            <Grid item xs={6} sm={6} md={6}>
                                <FormControl
                                    variant="outlined"
                                    sx={{ marginTop: 1 }}
                                    fullWidth
                                >
                                    <InputLabel
                                        htmlFor="pays"
                                        color="warning"
                                        shrink={CheckAttr(extractionDatas?.pays)}
                                    >
                                        Pays de production
                                    </InputLabel>
                                    <Select
                                        id="pays"
                                        native
                                        color="warning"
                                        value={extractionDatas?.pays}
                                        onChange={handleChange}
                                        label="Pays de production"
                                        inputProps={{
                                            name: "pays",
                                        }}
                                    >
                                        <option value=""></option>
                                        {dataPays.map((pays) => (
                                            <option key={pays?.id} value={pays?.id}>
                                                {pays?.libelle}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl
                                    variant="outlined"
                                    sx={{ marginTop: 1 }}
                                    fullWidth
                                >
                                    <InputLabel
                                        htmlFor="client"
                                        color="warning"
                                        shrink={CheckAttr(extractionDatas?.client)}
                                    >
                                        Client
                                    </InputLabel>
                                    <Select
                                        id="client"
                                        native
                                        color="warning"
                                        value={extractionDatas?.client}
                                        onChange={handleChange}
                                        label="Client"
                                        inputProps={{
                                            name: "client",
                                        }}
                                    >
                                        <option value=""></option>
                                        {dataClients.map((client) => (
                                            <option key={client?.id} value={client?.id}>
                                                {client?.libelle}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>

                                <TextField
                                    name="date_debut"
                                    label="Date debut"
                                    type="date"
                                    color="warning"
                                    fullWidth
                                    value={extractionDatas?.date_debut}
                                    error={errorInput?.date_debut}
                                    onChange={handleChange}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    sx={{ mt: 1 }}
                                />
                            </Grid>

                            <Grid item xs={6} sm={6} md={6}>
                                <FormControl
                                    variant="outlined"
                                    sx={{ marginTop: 1 }}
                                    fullWidth
                                >
                                    <InputLabel
                                        htmlFor="bu"
                                        color="warning"
                                        shrink={CheckAttr(extractionDatas?.bu)}
                                    >
                                        Business unit
                                    </InputLabel>
                                    <Select
                                        id="bu"
                                        native
                                        color="warning"
                                        value={extractionDatas?.bu}
                                        onChange={handleChange}
                                        label="Business unit"
                                        inputProps={{
                                            name: "bu",
                                        }}
                                    >
                                        <option value=""></option>
                                        {dataBusinessUnits.map((bu) => (
                                            <option key={bu?.id} value={bu?.id}>
                                                {bu?.libelle}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl
                                    variant="outlined"
                                    sx={{ marginTop: 1 }}
                                    fullWidth
                                >
                                    <InputLabel
                                        htmlFor="operation"
                                        color="warning"
                                        shrink={CheckAttr(extractionDatas?.operation)}
                                    >
                                        Opération
                                    </InputLabel>
                                    <Select
                                        id="operation"
                                        native
                                        color="warning"
                                        value={extractionDatas?.operation}
                                        onChange={handleChange}
                                        label="Opération"
                                        inputProps={{
                                            name: "operation",
                                        }}
                                    >
                                        <option value=""></option>
                                        {dataOperations.map((operation) => (
                                            <option key={operation?.id} value={operation?.id}>
                                                {operation?.libelle}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>

                                <TextField
                                    name="date_fin"
                                    label="Date fin"
                                    type="date"
                                    color="warning"
                                    fullWidth
                                    value={extractionDatas?.date_fin}
                                    error={errorInput?.date_fin}
                                    onChange={handleChange}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    sx={{ mt: 1 }}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>

                    <CardActions>
                        <Grid container spacing={2} columns={{ xs: 12, sm: 12, md: 12 }}>
                            <Grid item xs={12} sm={12} md={12}>
                                <Box display="flex" justifyContent="center">
                                    <Button
                                        variant="contained"
                                        color="warning"
                                        size="large"
                                        sx={{ textTransform: 'none' }}
                                        endIcon={<FileDownloadIcon />}
                                        onClick={() => handleExtract()}
                                    >
                                        Extraire
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </CardActions>
                </Card>
            </Container>

            {stackAlert}
            <div>
                <Backdrop
                    sx={{ color: "white", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={loader}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
            </div>
        </>
    );
};

export default ExtractionSaisie;